import React, { useState, useCallback, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import { withTranslation } from "react-i18next";
import constants from "../../../../constants";
import { COMPLIANCE_SUB_TAB } from "../tabs/constants";
import QuestionIcon from "assets/images/question_mark.svg";
import { usePolicySelection } from "../context/PolicySelectionContext";
import { useStaffTrackingSelection } from "../context/StaffTrackingSelectionContext";
import DeleteConfirmationModal from "components/DeleteConfirmationModal";
import PolicyHelpGuide from "./PolicyHelpGuide";
import styles from "../OfficePolicyManagement.module.scss";
import { useAccountOwnerCommon } from "lib/Context/AccountOwner";
import {
  useExportStaffTracking,
  staffTrackingReNotify,
  staffTrackingArchiveAssignments,
  cancelStaffTrackingAssignments,
} from "repositories/office-policy-management-repository";
import toast from "react-hot-toast";
import { b64toBlob, handleError } from "../../../../utils";
import FileSaver from "file-saver";
import moment from "moment";
import QuickPolicyModal from "./QuickPolicyModal";
import Loader from "components/Loader";
import { getLocalTimeZoneCode } from "accountOwner/pages/AddEvent/addEvents.functions";

const PolicyHeaderActions = ({
  t,
  canShowAddPolicy,
  complianceSubTab,
  className,
  history,
  staffTrackingPayload,
  staffTrackingTabActive,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isStaffDropdownOpen, setIsStaffDropdownOpen] = useState(false);
  const [showStaffCancelModal, setShowStaffCancelModal] = useState(false);
  const [staffBulkWorking, setStaffBulkWorking] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [isPolicyExporting, setIsPolicyExporting] = useState(false);
  const boxRef = useRef(null);
  const staffBoxRef = useRef(null);
  const { isStaff, dataAccessValues, isOwner, selectedOwnerIdString } =
    useAccountOwnerCommon();
  const canAccessThisPage = isOwner || (isStaff && dataAccessValues.isAdmin);
  const [isSaveEmployeeModalOpen, setIsSaveEmployeeModalOpen] = useState(false);
  const {
    selectedFolderPolicyIds,
    selectedPolicyIds,
    selectedPolicyMeta,
    archiveHandler,
    isArchiveTab,
    clearSelection,
    showDeleteModal,
    pendingDeleteIds,
    openDeleteModal,
    closeDeleteModal,
    deleteHandler,
    renotifyHandler,
    exportHandler,
  } = usePolicySelection();

  const {
    selectedAssignmentIds,
    pendingSelectedAssignmentIds,
    isArchivedStaffList,
    hasStaffSelection,
    hasAnySelectedPending,
    clearStaffSelection,
    refetchStaffTracking,
  } = useStaffTrackingSelection();

  const hasSelection = selectedFolderPolicyIds.length > 0;
  const canDelete =
    hasSelection && (selectedPolicyMeta?.hasAnyDeletable ?? false);
  const canRenotifySelected =
    hasSelection && (selectedPolicyMeta?.hasAnyRenotifiable ?? false);
  const canRenewAcknowledgementSelected =
    hasSelection && (selectedPolicyMeta?.hasAnyRenewable ?? false);
  const shouldDisableRenewAcknowledgement =
    hasSelection && !canRenewAcknowledgementSelected;

  const isOnArchivedTab =
    isArchiveTab || complianceSubTab === COMPLIANCE_SUB_TAB.archivedPolicies;

  const canStaffArchiveBulk = hasStaffSelection;
  /** Re-notify / Cancel enabled when there is a selection and at least one row is Pending. */
  const canStaffRenotifyBulk = hasStaffSelection && hasAnySelectedPending;
  const canStaffCancelBulk = hasStaffSelection && hasAnySelectedPending;

  const toggle = useCallback(() => setIsDropdownOpen((prev) => !prev), []);
  const toggleStaff = useCallback(
    () => setIsStaffDropdownOpen((prev) => !prev),
    [],
  );

  const { timeZoneId } = getLocalTimeZoneCode();

  const handleOutsideClick = useCallback((e) => {
    if (boxRef.current && !boxRef.current.contains(e.target)) {
      setIsDropdownOpen(false);
    }
    if (staffBoxRef.current && !staffBoxRef.current.contains(e.target)) {
      setIsStaffDropdownOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [handleOutsideClick]);

  const handleBulkArchive = useCallback(
    (e) => {
      e?.preventDefault?.();
      e?.stopPropagation?.();
      if (!hasSelection) return;
      setIsDropdownOpen(false);
      const ids = [...selectedFolderPolicyIds];
      archiveHandler(ids);
      clearSelection();
    },
    [hasSelection, archiveHandler, selectedFolderPolicyIds, clearSelection],
  );

  const handleBulkRenotify = useCallback(
    (e) => {
      e?.preventDefault?.();
      e?.stopPropagation?.();
      if (!canRenotifySelected) return;
      setIsDropdownOpen(false);
      renotifyHandler([...selectedPolicyIds]);
      clearSelection();
    },
    [canRenotifySelected, renotifyHandler, selectedPolicyIds, clearSelection],
  );

  const redirectToRenewAcknowledgement = useCallback(() => {
    setIsDropdownOpen(false);
    history.push({
      pathname: constants.routes.officePolicyManagements.renewAcknowledgement,
      search: selectedOwnerIdString ? `?${selectedOwnerIdString}` : "",
      state: {
        renewPreselectedFolderIds: [...selectedFolderPolicyIds],
        renewFromArchived: isOnArchivedTab,
      },
    });
  }, [
    selectedOwnerIdString,
    history,
    selectedFolderPolicyIds,
    isOnArchivedTab,
  ]);

  const redirectToPolicyLogs = useCallback(() => {
    setIsDropdownOpen(false);
    history.push({
      pathname: constants.routes.officePolicyManagements.policyLogsListing,
      search: selectedOwnerIdString ? `?${selectedOwnerIdString}` : "",
    });
  }, [selectedOwnerIdString]);
  const secureFileDownload = (
    base64Content,
    fileName,
    contentType = "application/octet-stream",
    t,
  ) => {
    try {
      // Validate inputs
      if (!base64Content || !fileName) {
        throw new Error(t("aiAssistant.invalidFile"));
      }

      // Sanitize filename to prevent path traversal attacks
      const sanitizedFileName = fileName.replace(/[^a-zA-Z0-9._-]/g, "_");

      // Use existing safe utility function
      const blob = b64toBlob(base64Content, contentType);

      // Create a safe download using FileSaver
      FileSaver.saveAs(blob, sanitizedFileName);

      return true;
    } catch (error) {
      handleError(t("aiAssistant.fileDownloadError"));
      return false;
    }
  };
  const exportStaffTrackingQuery = useExportStaffTracking(
    { params: staffTrackingPayload, timeZoneId },
    {
      onSuccess: (e) => {
        if (e?.data?.file_Url) {
          const success = secureFileDownload(
            e.data.file_Url,
            `staff-tracking-history-${moment().toISOString()}.xlsx`,
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            t,
          );
          if (success) {
            toast.success(
              t(e?.message || "officePolicyManagement.exportSuccessful"),
            );
          } else {
            toast.error(t(e?.message || "officePolicyManagement.exportFailed"));
          }
        } else {
          toast.error(t(e?.message || "officePolicyManagement.exportFailed"));
        }
      },
      onError: (e) => {
        toast.error(t(e?.message || "officePolicyManagement.exportFailed"));
      },
    },
  );
  const handleExportForStaffTracking = useCallback(() => {
    exportStaffTrackingQuery.mutate();
  }, []);

  const handleStaffBulkArchive = useCallback(
    async (e) => {
      e?.preventDefault?.();
      e?.stopPropagation?.();
      if (!canStaffArchiveBulk) return;
      setIsStaffDropdownOpen(false);
      setStaffBulkWorking(true);
      try {
        const wantArchive = !isArchivedStaffList;
        await staffTrackingArchiveAssignments(
          selectedAssignmentIds,
          wantArchive,
        );
        toast.success(
          t(
            wantArchive
              ? "officePolicyManagement.archiveSuccess"
              : "officePolicyManagement.unarchiveSuccess",
          ),
        );
        clearStaffSelection();
        refetchStaffTracking();
      } catch (err) {
        toast.error(
          err?.message || t("officePolicyManagement.archiveUnarchiveFailed"),
        );
      } finally {
        setStaffBulkWorking(false);
      }
    },
    [
      canStaffArchiveBulk,
      isArchivedStaffList,
      selectedAssignmentIds,
      t,
      clearStaffSelection,
      refetchStaffTracking,
    ],
  );

  const handleStaffBulkRenotify = useCallback(
    async (e) => {
      e?.preventDefault?.();
      e?.stopPropagation?.();
      if (!canStaffRenotifyBulk) return;
      setIsStaffDropdownOpen(false);
      setStaffBulkWorking(true);
      try {
        await staffTrackingReNotify(pendingSelectedAssignmentIds);
        toast.success(t("officePolicyManagement.renotifySent"));
        clearStaffSelection();
        refetchStaffTracking();
      } catch (err) {
        toast.error(
          err?.message || t("officePolicyManagement.failedToSendRenotify"),
        );
      } finally {
        setStaffBulkWorking(false);
      }
    },
    [
      canStaffRenotifyBulk,
      pendingSelectedAssignmentIds,
      t,
      clearStaffSelection,
      refetchStaffTracking,
    ],
  );

  const handleStaffBulkCancelConfirm = useCallback(async () => {
    if (!pendingSelectedAssignmentIds.length) return;
    setStaffBulkWorking(true);
    try {
      const result = await cancelStaffTrackingAssignments(
        pendingSelectedAssignmentIds,
      );
      if (result?.message) toast.success(result.message);
      setShowStaffCancelModal(false);
      clearStaffSelection();
      refetchStaffTracking();
    } catch (err) {
      toast.error(err?.message || t("officePolicyManagement.archiveFailed"));
    } finally {
      setStaffBulkWorking(false);
    }
  }, [
    pendingSelectedAssignmentIds,
    t,
    clearStaffSelection,
    refetchStaffTracking,
  ]);

  return (
    <div
      className={`d-flex align-items-center justify-content-end ${styles.policy_header_actions} ${className || ""}`}
    >
      <button
        type="button"
        className={styles.policy_question_btn}
        onClick={() => setIsHelpOpen(true)}
        aria-label={t("navbar.help")}
      >
        <img src={QuestionIcon} alt={t("navbar.help")} />
        <span className="m-0">{t("navbar.help")}</span>
      </button>
      {staffTrackingTabActive && (
        <>
          <div className={styles.policy_header_separator} />
          <div
            className={`${styles.policy_actions_dropdown} cursor-pointer`}
            onClick={toggleStaff}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                toggleStaff();
              }
            }}
            role="button"
            tabIndex={0}
            ref={staffBoxRef}
          >
            <Dropdown isOpen={isStaffDropdownOpen} toggle={() => {}}>
              <DropdownToggle caret={false} tag="div">
                <span className="ico">
                  <img
                    src={require("assets/images/dots-icon.svg").default}
                    alt=""
                  />
                </span>
              </DropdownToggle>
              <DropdownMenu end>
                <DropdownItem
                  disabled={!canStaffArchiveBulk || staffBulkWorking}
                  onClick={(e) => handleStaffBulkArchive(e)}
                >
                  <span>
                    {isArchivedStaffList
                      ? t("officePolicyManagement.staffTrackingUnarchive")
                      : t("officePolicyManagement.archive")}
                  </span>
                </DropdownItem>
                <DropdownItem
                  disabled={!canStaffRenotifyBulk || staffBulkWorking}
                  onClick={(e) => handleStaffBulkRenotify(e)}
                >
                  <span>{t("officePolicyManagement.renotify")}</span>
                </DropdownItem>
                <DropdownItem
                  disabled={!canStaffCancelBulk || staffBulkWorking}
                  onClick={(e) => {
                    e?.preventDefault?.();
                    e?.stopPropagation?.();
                    if (!canStaffCancelBulk) return;
                    setIsStaffDropdownOpen(false);
                    setShowStaffCancelModal(true);
                  }}
                >
                  <span>{t("officePolicyManagement.cancel")}</span>
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </div>
          <div className={styles.policy_header_separator} />
          <button
            type="button"
            className={`button button-round button-shadow ${styles.policy_add_btn}`}
            onClick={() => handleExportForStaffTracking()}
            disabled={
              exportStaffTrackingQuery.isLoading ||
              exportStaffTrackingQuery.isFetching
            }
          >
            <span>
              {exportStaffTrackingQuery.isLoading ||
              exportStaffTrackingQuery.isFetching
                ? "Exporting..."
                : t("officePolicyManagement.export")}
            </span>
          </button>
        </>
      )}
      {!staffTrackingTabActive && (
        <>
          <div className={styles.policy_header_separator} />
          <div
            className={`${styles.policy_actions_dropdown} cursor-pointer`}
            onClick={toggle}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                toggle();
              }
            }}
            role="button"
            tabIndex={0}
            ref={boxRef}
          >
            <Dropdown isOpen={isDropdownOpen} toggle={() => {}}>
              <DropdownToggle caret={false} tag="div">
                <span className="ico">
                  <img
                    src={require("assets/images/dots-icon.svg").default}
                    alt=""
                  />
                </span>
              </DropdownToggle>
              <DropdownMenu end>
                <DropdownItem
                  disabled={hasSelection}
                  onClick={() => {
                    if (hasSelection) return;
                    setIsSaveEmployeeModalOpen(true);
                  }}
                >
                  <span>{t("officePolicyManagement.quickPolicySetup")}</span>
                </DropdownItem>
                <DropdownItem
                  disabled={shouldDisableRenewAcknowledgement}
                  onClick={() => {
                    if (shouldDisableRenewAcknowledgement) return;
                    redirectToRenewAcknowledgement();
                  }}
                >
                  <span>
                    {t("officePolicyManagement.renewAcknowledgement")}
                  </span>
                </DropdownItem>
                <DropdownItem
                  disabled={hasSelection}
                  onClick={() => {
                    if (hasSelection) return;
                    redirectToPolicyLogs();
                  }}
                >
                  <span>{t("officePolicyManagement.policyLogs")}</span>
                </DropdownItem>
                <DropdownItem
                  disabled={!canDelete}
                  onClick={(e) => {
                    e?.preventDefault?.();
                    e?.stopPropagation?.();
                    if (!canDelete) return;
                    setIsDropdownOpen(false);
                    openDeleteModal(selectedFolderPolicyIds);
                  }}
                >
                  <span>{t("officePolicyManagement.delete")}</span>
                </DropdownItem>
                <DropdownItem
                  disabled={!hasSelection}
                  onClick={(e) => handleBulkArchive(e)}
                >
                  <span>
                    {isOnArchivedTab
                      ? t("officePolicyManagement.unarchivePolicy")
                      : t("officePolicyManagement.archivePolicy")}
                  </span>
                </DropdownItem>
                <DropdownItem
                  disabled={!canRenotifySelected}
                  onClick={(e) => handleBulkRenotify(e)}
                >
                  <span>{t("officePolicyManagement.renotify")}</span>
                </DropdownItem>
                <DropdownItem
                  disabled={hasSelection}
                  onClick={async (e) => {
                    e?.preventDefault?.();
                    e?.stopPropagation?.();
                    if (hasSelection) return;
                    setIsDropdownOpen(false);
                    if (typeof exportHandler !== "function") return;
                    try {
                      setIsPolicyExporting(true);
                      await exportHandler();
                    } finally {
                      setIsPolicyExporting(false);
                    }
                  }}
                >
                  <span>{t("officePolicyManagement.export")}</span>
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </div>
        </>
      )}
      {canShowAddPolicy && !staffTrackingTabActive && (
        <Link
          to={{
            pathname:
              constants.routes.officePolicyManagements
                .officePolicyManagementAddPolicyFolder,
            search: selectedOwnerIdString ? `?${selectedOwnerIdString}` : "",
          }}
          className={`button button-round button-shadow ${styles.policy_add_btn}`}
          title={t("officePolicyManagement.addPolicy")}
        >
          {t("officePolicyManagement.addPolicy")}
        </Link>
      )}
      {isHelpOpen && (
        <PolicyHelpGuide isOpen={isHelpOpen} onClose={setIsHelpOpen} />
      )}
      {(isPolicyExporting || staffBulkWorking) && <Loader />}
      <DeleteConfirmationModal
        confirmDelete={showDeleteModal}
        closeDeleteModal={closeDeleteModal}
        confirmDeleteMessage={() => deleteHandler(pendingDeleteIds)}
        deleteMessage={t("officePolicyManagement.deleteMsg")}
        title={`${t("officePolicyManagement.deletePolicy")}?`}
        btnText={t("officePolicyManagement.delete")}
      />
      <DeleteConfirmationModal
        confirmDelete={showStaffCancelModal}
        closeDeleteModal={() => setShowStaffCancelModal(false)}
        confirmDeleteMessage={handleStaffBulkCancelConfirm}
        deleteMessage={t("officePolicyManagement.cancelConfirmation")}
        title={t("officePolicyManagement.cancelPolicy")}
        btnText={t("officePolicyManagement.yesCancel")}
        isLoading={staffBulkWorking}
      />
      {isSaveEmployeeModalOpen && (
        <QuickPolicyModal
          t={t}
          issaveEmployeeModalOpen={isSaveEmployeeModalOpen}
          setIsSaveEmployeeModalOpen={setIsSaveEmployeeModalOpen}
        />
      )}
    </div>
  );
};

export default withTranslation()(PolicyHeaderActions);
